import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminTenant } from '../../types/domain';
import { DataTableColumn, FilterRule } from '../../types/grid';
import { SortingState } from '@tanstack/react-table';
import { DataTable, BulkAction } from '../grid/DataTable';
import { IdCell } from '../grid/IdCell';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Shield,
  ShieldAlert,
  Check,
  Copy,
  ArrowRight,
  Trash2,
  AlertTriangle,
  Loader2,
  X,
} from 'lucide-react';

interface AdminTenantsGridProps {
  tenants: AdminTenant[];
  totalRows?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void;
  onSearchChange?: (search: string) => void;
  onSortChange?: (field: string, dir: 'asc' | 'desc' | '', allSorts?: SortingState) => void;
  sortRules?: SortingState;
  onFilterChange?: (rules: FilterRule[]) => void;
  filterRules?: FilterRule[];
  searchValue?: string;
  isLoading?: boolean;
  onDeleteTenant?: (tenant: AdminTenant) => Promise<void>;
  onBulkDeleteTenants?: (tenants: AdminTenant[]) => Promise<void>;
  currentUserId?: string;
  onRefresh: () => void;
  isRefreshing: boolean;
  persistenceKey?: string;
}

interface BulkDeleteTenantsBannerProps {
  targets: AdminTenant[];
  currentUserId?: string;
  isDeleting: boolean;
  onConfirm: (targets: AdminTenant[]) => Promise<void>;
  onCancel: () => void;
}

/**
 * Isolated Bulk Delete Confirmation Banner for zero-lag 60fps typing.
 * Handles single or high-volume (up to 250+) tenant selections with badge clamping
 * and automatic self-deletion lockout prevention.
 */
const BulkDeleteTenantsBanner: React.FC<BulkDeleteTenantsBannerProps> = ({
  targets,
  currentUserId,
  isDeleting,
  onConfirm,
  onCancel,
}) => {
  const [input, setInput] = useState('');
  const [showAllBadges, setShowAllBadges] = useState(false);

  // Filter out the active user themselves so they cannot delete their own session
  const validTargets = useMemo(
    () => targets.filter((u) => !currentUserId || u.id !== currentUserId),
    [targets, currentUserId]
  );
  const hasSelf = currentUserId && targets.some((u) => u.id === currentUserId);

  const requiredPhrase = 'DELETE PERMANENTLY';
  const isConfirmed = input.trim() === requiredPhrase;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isConfirmed || isDeleting || validTargets.length === 0) return;
    onConfirm(validTargets);
  };

  const visibleBadges = showAllBadges ? targets : targets.slice(0, 8);
  const remainingCount = targets.length - 8;

  return (
    <div className="mx-4 mt-3 p-4 bg-rose-500/10 border border-rose-500/30 rounded-lg space-y-3 shrink-0">
      {/* Header Info */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="p-2 rounded bg-rose-500/20 text-rose-600 dark:text-rose-400 mt-0.5 shrink-0">
            <AlertTriangle size={18} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-rose-700 dark:text-rose-300">
              Confirm Permanent User Deletion ({validTargets.length}{' '}
              {validTargets.length === 1 ? 'account' : 'accounts'})
            </h3>
            <p className="text-rose-600/90 dark:text-rose-400/90 text-xs mt-0.5">
              This will permanently delete the selected {targets.length === 1 ? 'user account' : 'user accounts'} from the platform.
              All organization memberships, project roles, table permissions, and active API session tokens will be permanently revoked.
            </p>
            {hasSelf && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-amber-500/20 border border-amber-500/40 text-amber-700 dark:text-amber-300 font-medium text-xs">
                <span>
                  {validTargets.length === 0
                    ? '⚠️ Your active account is the only user selected. Self-deletion is strictly prevented.'
                    : '⚠️ Note: Your own active account is included in this selection and will be automatically skipped to prevent self-lockout.'}
                </span>
              </div>
            )}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onCancel}
          disabled={isDeleting}
          className="text-cf-textMuted hover:text-cf-textPrimary"
          title="Cancel deletion"
        >
          <X size={16} />
        </Button>
      </div>

      {/* Badges of selected users with high-volume clamp */}
      <div className="flex items-center gap-1.5 flex-wrap max-h-28 overflow-y-auto py-1">
        {visibleBadges.map((u) => {
          const isSelfUser = currentUserId && u.id === currentUserId;
          return (
            <span
              key={u.id}
              className={`px-2 py-0.5 rounded font-sans text-xs border truncate max-w-[220px] ${
                isSelfUser
                  ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/40 line-through'
                  : 'bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-500/30'
              }`}
              title={`${u.full_name || 'Unnamed'} (${u.email})${isSelfUser ? ' [Protected / Self]' : ''}`}
            >
              {u.full_name || 'Unnamed'} ({u.email})
            </span>
          );
        })}
        {targets.length > 8 && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => setShowAllBadges((prev) => !prev)}
            className="bg-rose-500/15 hover:bg-rose-500/25 text-rose-700 dark:text-rose-300 border border-rose-500/30 font-semibold"
          >
            {showAllBadges ? 'Show fewer' : `+${remainingCount} more...`}
          </Button>
        )}
      </div>

      {/* Form Action Bar */}
      <form onSubmit={handleSubmit} className="flex items-center gap-3 flex-wrap pt-1">
        <div className="flex items-center gap-2">
          <label className="text-cf-textSecondary text-xs uppercase font-semibold">
            Type{' '}
            <strong className="text-cf-textPrimary font-sans">
              {requiredPhrase}
            </strong>{' '}
            to confirm:
          </label>
          <Input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={requiredPhrase}
            disabled={isDeleting || validTargets.length === 0}
            className="w-44 font-sans text-xs focus:border-rose-500"
            autoFocus
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            variant="danger"
            size="sm"
            disabled={isDeleting || !isConfirmed || validTargets.length === 0}
            loading={isDeleting}
            icon={isDeleting ? <Loader2 size={12} className="animate-spin" /> : undefined}
          >
            {isDeleting
              ? 'Deleting...'
              : `Permanently Delete ${validTargets.length} ${
                  validTargets.length === 1 ? 'User' : 'Users'
                }`}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export const AdminTenantsGrid: React.FC<AdminTenantsGridProps> = ({
  tenants,
  totalRows,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onSortChange,
  sortRules,
  onFilterChange,
  filterRules,
  searchValue,
  isLoading = false,
  onDeleteTenant,
  onBulkDeleteTenants,
  currentUserId,
  onRefresh,
  isRefreshing,
  persistenceKey = 'admin_tenants',
}) => {
  const navigate = useNavigate();
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  // Selection & Bulk Delete state
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [bulkDeleteTargets, setBulkDeleteTargets] = useState<AdminTenant[] | null>(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const handleCopyEmail = (email: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(email);
    setCopiedEmail(email);
    setTimeout(() => setCopiedEmail(null), 2000);
  };

  const handleExecuteBulkDelete = async (targets: AdminTenant[]) => {
    setIsBulkDeleting(true);
    try {
      if (targets.length === 1 && onDeleteTenant) {
        await onDeleteTenant(targets[0]);
      } else if (onBulkDeleteTenants) {
        await onBulkDeleteTenants(targets);
      }
      setBulkDeleteTargets(null);
      setSelectedRowIds([]);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const columns = useMemo<DataTableColumn<AdminTenant>[]>(
    () => [
      {
        id: 'id',
        header: 'ID',
        accessorKey: 'id',
        width: 110,
        minWidth: 110,
        maxWidth: 110,
        sortable: true,
        filterable: true,
        pinned: 'left',
        isSystem: true,
        cell: ({ row }) => <IdCell id={row.id} />,
      },
      {
        id: 'full_name',
        header: 'Name',
        accessorKey: 'full_name',
        width: 220,
        minWidth: 180,
        maxWidth: 300,
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <div className="truncate">
            <span
              className="font-sans font-medium text-cf-textPrimary text-xs truncate block"
              title={row.full_name}
            >
              {row.full_name || 'Unnamed User'}
            </span>
            {row.primary_org_name && (
              <span
                className="text-xs text-cf-textMuted truncate block"
                title={`Primary Org: ${row.primary_org_name}`}
              >
                {row.primary_org_name}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'email',
        header: 'Email Address',
        accessorKey: 'email',
        width: 220,
        minWidth: 180,
        maxWidth: 280,
        dataType: 'email',
        sortable: true,
        filterable: true,
        cell: ({ row }) => {
          const isCopied = copiedEmail === row.email;
          return (
            <div className="flex items-center justify-between group/cell w-full truncate">
              <span className="text-xs text-cf-textPrimary truncate" title={row.email}>
                {row.email}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={(e) => handleCopyEmail(row.email, e)}
                className="opacity-0 group-hover/cell:opacity-100 text-cf-textMuted hover:text-cf-orange transition-opacity"
                title="Copy Email"
              >
                {isCopied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
              </Button>
            </div>
          );
        },
      },
      {
        id: 'is_active',
        header: 'Status',
        accessorFn: (row) => (row.is_active ? 'active' : 'suspended'),
        width: 110,
        minWidth: 100,
        maxWidth: 130,
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase border ${
              row.is_active
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                : 'bg-rose-500/10 border-rose-500/30 text-rose-600 dark:text-rose-400'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                row.is_active ? 'bg-emerald-500' : 'bg-rose-500'
              }`}
            />
            <span>{row.is_active ? 'Active' : 'Suspended'}</span>
          </span>
        ),
      },
      {
        id: 'is_superadmin',
        header: 'Platform Role',
        accessorFn: (row) => (row.is_superadmin ? 'superadmin' : 'tenant'),
        width: 120,
        minWidth: 110,
        maxWidth: 140,
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase border ${
              row.is_superadmin
                ? 'bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400'
                : 'bg-zinc-500/10 border-zinc-500/30 text-zinc-500 dark:text-zinc-400'
            }`}
          >
            {row.is_superadmin ? <ShieldAlert size={12} /> : <Shield size={12} />}
            <span>{row.is_superadmin ? 'Superadmin' : 'User'}</span>
          </span>
        ),
      },
      {
        id: 'organizations_count',
        header: 'Organizations',
        accessorFn: (row) => row.organizations_count,
        width: 120,
        minWidth: 100,
        maxWidth: 140,
        dataType: 'number',
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <span className="text-xs text-cf-textPrimary tabular-nums">
            {row.organizations_count ?? 0}
          </span>
        ),
      },
      {
        id: 'tables_count',
        header: 'Datastores',
        accessorFn: (row) => row.tables_count,
        width: 110,
        minWidth: 90,
        maxWidth: 130,
        dataType: 'number',
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <span className="text-xs text-cf-textSecondary tabular-nums">
            {row.tables_count ?? 0}
          </span>
        ),
      },
      {
        id: 'submissions_count',
        header: 'Submissions',
        accessorFn: (row) => row.submissions_count,
        width: 130,
        minWidth: 110,
        maxWidth: 160,
        dataType: 'number',
        sortable: true,
        filterable: true,
        cell: ({ row }) => (
          <span className="text-xs text-cf-textPrimary tabular-nums font-medium">
            {(row.submissions_count || 0).toLocaleString()}
          </span>
        ),
      },
      {
        id: 'created_at',
        header: 'Created At',
        accessorKey: 'created_at',
        width: 180,
        minWidth: 160,
        maxWidth: 220,
        dataType: 'date',
        sortable: true,
        filterable: true,
      },
      {
        id: 'actions',
        header: 'Actions',
        width: 100,
        minWidth: 96,
        maxWidth: 120,
        sortable: false,
        filterable: false,
        pinned: 'right',
        isSystem: true,
        cell: ({ row }) => {
          const isSelf = currentUserId && row.id === currentUserId;
          return (
            <div className="flex items-center justify-end gap-1 px-1">
              {onDeleteTenant && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  disabled={Boolean(isSelf)}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSelf) return;
                    setBulkDeleteTargets([row]);
                  }}
                  className={
                    isSelf
                      ? 'text-cf-textMuted/30 cursor-not-allowed'
                      : 'text-cf-textMuted hover:text-rose-400'
                  }
                  title={
                    isSelf
                      ? 'You cannot delete your own account'
                      : `Delete User: ${row.full_name || row.email}`
                  }
                >
                  <Trash2 size={13} />
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => navigate(`/admin/tenants/${row.id}`)}
                className="text-cf-textSecondary hover:text-cf-textPrimary"
                title="Manage User Detail"
              >
                <ArrowRight size={14} />
              </Button>
            </div>
          );
        },
      },
    ],
    [navigate, copiedEmail, onDeleteTenant, currentUserId]
  );

  const bulkActions = useMemo<BulkAction<AdminTenant>[] | undefined>(() => {
    if (!onDeleteTenant && !onBulkDeleteTenants) return undefined;
    return [
      {
        label: `Delete Selected (${selectedRowIds.length})`,
        icon: <Trash2 size={13} />,
        variant: 'danger',
        onClick: (selectedRows: AdminTenant[]) => {
          setBulkDeleteTargets(selectedRows);
        },
      },
    ];
  }, [onDeleteTenant, onBulkDeleteTenants, selectedRowIds.length]);

  return (
    <div className="flex-1 min-h-0 min-w-0 w-full max-w-full overflow-hidden flex flex-col">
      {/* In-Page Mass Deletion Confirmation Banner (Isolated subcomponent for 60fps zero-lag typing) */}
      {bulkDeleteTargets && bulkDeleteTargets.length > 0 && (
        <BulkDeleteTenantsBanner
          targets={bulkDeleteTargets}
          currentUserId={currentUserId}
          isDeleting={isBulkDeleting}
          onConfirm={handleExecuteBulkDelete}
          onCancel={() => setBulkDeleteTargets(null)}
        />
      )}

      {/* Main Server-Paginated Table with Checkbox Row Selection */}
      <DataTable<AdminTenant>
        data={tenants}
        columns={columns}
        tableName="admin-tenants"
        persistenceKey={persistenceKey}
        serverSide={true}
        totalRows={totalRows}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onServerSearchChange={onSearchChange}
        onServerSortChange={onSortChange}
        initialSorting={sortRules}
        onServerFilterChange={onFilterChange}
        initialFilterRules={filterRules}
        searchValue={searchValue}
        isLoading={isLoading}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        enableSelection={true}
        selectedRowIds={selectedRowIds}
        onSelectionChange={setSelectedRowIds}
        bulkActions={bulkActions}
        emptyTitle="No users found"
        emptySubtitle="Try adjusting your search or status filter."
        onRowClick={(row) => navigate(`/admin/tenants/${row.id}`)}
      />
    </div>
  );
};
